import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { ArrowLeft, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { resolvePostVisibility, slugifyVi, type PostVisibility } from '@repo/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/src/components/ui/select';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import {
  isoToLocalInput,
  localInputToIso,
  useAdminBlogAuthors,
  useAdminBlogPost,
  useBlogPreview,
  useCreateBlogPost,
  useDeleteBlogPost,
  useNow,
  useUpdateBlogPost,
  useUploadBlogMedia,
} from '../hooks/useBlog';
import { formatDateTime } from '../lib/adminFormat';

const VISIBILITY_LABEL: Record<PostVisibility, string> = {
  draft: 'Bản nháp',
  scheduled: 'Đã lên lịch',
  live: 'Đang hiển thị',
};

const VISIBILITY_VARIANT: Record<PostVisibility, 'muted' | 'warning' | 'success'> = {
  draft: 'muted',
  scheduled: 'warning',
  live: 'success',
};

const PREVIEW_DEBOUNCE_MS = 400;

const HOUSE_STYLE_RULES = `Quy tắc chung khi viết bài cho blog LopHoc.app:
- Viết bằng tiếng Việt, giọng thân thiện, chuyên nghiệp, dễ hiểu cho giáo viên/gia sư tự do.
- Câu ngắn, đoạn ngắn (3-4 câu); ưu tiên ví dụ cụ thể và số liệu thực tế thay vì lời khẳng định chung.
- Không phóng đại, không hứa hẹn kết quả chưa kiểm chứng được.
- Mỗi bài có một ý chính rõ ràng, mở đầu bằng lý do người đọc nên quan tâm.`;

const MARKDOWN_CONVENTIONS = `Quy ước Markdown:
- Dùng GFM: tiêu đề (#, ##, ###), đậm/nghiêng, liên kết, danh sách, blockquote, bảng, khối mã.
- Không chèn HTML thô, thẻ <script>, thuộc tính on*, hay liên kết javascript: — bộ lọc sẽ loại bỏ chúng.
- Ảnh chèn trong bài dùng cú pháp ![](url); dán hoặc kéo-thả ảnh vào khung soạn để tự động tải lên và chèn tại vị trí con trỏ.`;

/** Rendered-preview styling done with utility classes rather than a
 *  typography plugin (not installed in this app) or a highlighter (removed
 *  from the markdown pipeline on purpose — `<pre><code class="language-x">`
 *  stays unstyled, see docs/design/004-Blog-Design.md §5). */
const PREVIEW_CLASSNAME =
  'max-w-none text-sm leading-relaxed text-surface-800 ' +
  '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-semibold ' +
  '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold ' +
  '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold ' +
  '[&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 ' +
  '[&_a]:text-brand-700 [&_a]:underline ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:border-surface-300 [&_blockquote]:pl-3 [&_blockquote]:text-surface-600 [&_blockquote]:italic ' +
  '[&_code]:rounded [&_code]:bg-surface-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em] ' +
  '[&_pre]:mb-3 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-surface-100 [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 ' +
  '[&_table]:mb-3 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-surface-200 [&_th]:px-2 [&_th]:py-1 [&_td]:border [&_td]:border-surface-200 [&_td]:px-2 [&_td]:py-1 ' +
  '[&_img]:my-3 [&_img]:max-w-full [&_hr]:my-4 [&_hr]:border-surface-200';

export function BlogPostEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const now = useNow();

  const postQuery = useAdminBlogPost(isNew ? undefined : id);
  const authorsQuery = useAdminBlogAuthors();
  const createPost = useCreateBlogPost();
  const updatePost = useUpdateBlogPost(id ?? '');
  const deletePost = useDeleteBlogPost();
  const uploadMedia = useUploadBlogMedia();
  const preview = useBlogPreview();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [excerpt, setExcerpt] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [authorId, setAuthorId] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [publishInput, setPublishInput] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hydratedIdRef = useRef<string | null>(null);

  // Hydrate the form once per post id — a later refetch (e.g. after Lưu
  // invalidates the list query) must not stomp on further edits the admin
  // has made in the meantime.
  useEffect(() => {
    const post = postQuery.data;
    if (!post || hydratedIdRef.current === post.id) return;
    hydratedIdRef.current = post.id;
    setTitle(post.title);
    setSlug(post.slug);
    // An existing post's slug was already a deliberate choice — don't let a
    // title edit silently rewrite it.
    setSlugTouched(true);
    setExcerpt(post.excerpt);
    setTagsInput(post.tags.join(', '));
    setCoverUrl(post.coverUrl ?? '');
    setAuthorId(post.authorId);
    setBodyMd(post.bodyMd);
    setPublishInput(isoToLocalInput(post.publishedAt));
  }, [postQuery.data]);

  // Default a brand-new post to the first available author once the list
  // loads, so the required field isn't left silently empty.
  useEffect(() => {
    const firstAuthor = authorsQuery.data?.[0];
    if (!authorId && firstAuthor) {
      setAuthorId(firstAuthor.id);
    }
  }, [authorsQuery.data, authorId]);

  // Debounced stateless preview — 400ms after the last keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      preview.mutate(bodyMd, { onSuccess: (data) => setPreviewHtml(data.html) });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyMd]);

  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const targetPublishTime = publishInput ? new Date(publishInput).getTime() : now.getTime();
  const willBeFuture = targetPublishTime > now.getTime();

  const currentVisibility: PostVisibility | null = postQuery.data
    ? resolvePostVisibility(postQuery.data, now)
    : null;

  const authors = authorsQuery.data ?? [];
  const selectedAuthor = authors.find((a) => a.id === authorId);

  function handleTitleChange(value: string) {
    setTitle(value);
    if (!slugTouched) setSlug(slugifyVi(value));
  }

  function handleSlugChange(value: string) {
    setSlugTouched(true);
    setSlug(value);
  }

  function buildBasePayload() {
    return {
      slug: slug.trim(),
      title: title.trim(),
      excerpt: excerpt.trim(),
      bodyMd,
      coverUrl: coverUrl.trim() ? coverUrl.trim() : null,
      tags: tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      authorId,
    };
  }

  const canSave = !!(title.trim() && slug.trim() && excerpt.trim() && bodyMd.trim() && authorId);
  const isSaving = createPost.isPending || updatePost.isPending;

  async function handleSave() {
    if (!canSave) return;
    if (isNew) {
      const created = await createPost.mutateAsync({ ...buildBasePayload(), status: 'draft' });
      toast.success('Đã tạo bản nháp');
      navigate(`/blog/${created.id}`, { replace: true });
    } else {
      await updatePost.mutateAsync(buildBasePayload());
      toast.success('Đã lưu');
    }
  }

  async function handlePublish() {
    if (!id || !canSave) return;
    const targetIso = localInputToIso(publishInput) ?? now.toISOString();
    await updatePost.mutateAsync({
      ...buildBasePayload(),
      status: 'published',
      publishedAt: new Date(targetIso),
    });
    setPublishInput(isoToLocalInput(targetIso));
    toast.success(willBeFuture ? 'Đã lên lịch đăng bài' : 'Đã đăng bài');
  }

  async function handleUnpublish() {
    if (!id) return;
    await updatePost.mutateAsync({ ...buildBasePayload(), status: 'draft' });
    toast.success('Đã gỡ bài viết xuống');
  }

  async function handleDelete() {
    if (!id) return;
    await deletePost.mutateAsync(id);
    navigate('/blog');
  }

  /** Where to insert: `[start, end)` in the textarea's value, replacing any
   *  active selection. Must be read synchronously off the DOM inside the
   *  paste/drop handler itself — never after an `await` — because an upload
   *  takes real time and nothing stops the user from typing or re-focusing
   *  elsewhere while it's in flight. `selectionStart`/`selectionEnd` are
   *  always numbers on a textarea (0/0 if it's never been focused), so this
   *  never needs a null fallback. */
  function captureCaretRange(): { start: number; end: number } {
    const el = textareaRef.current;
    if (!el) {
      const end = bodyMd.length;
      return { start: end, end };
    }
    return { start: el.selectionStart, end: el.selectionEnd };
  }

  function insertAtRange(insertText: string, range: { start: number; end: number }) {
    const el = textareaRef.current;
    setBodyMd((prev) => {
      const start = Math.min(range.start, prev.length);
      const end = Math.min(range.end, prev.length);
      return prev.slice(0, start) + insertText + prev.slice(end);
    });
    if (el) {
      requestAnimationFrame(() => {
        const pos = range.start + insertText.length;
        el.setSelectionRange(pos, pos);
        el.focus();
      });
    }
  }

  async function uploadAndInsertImage(file: File, range: { start: number; end: number }) {
    try {
      const { url } = await uploadMedia.mutateAsync(file);
      insertAtRange(`![](${url})`, range);
    } catch {
      // useUploadBlogMedia already toasts the error.
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const item = Array.from(e.clipboardData.items).find((it) => it.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (!file) return;
    e.preventDefault();
    // Capture where the caret/selection is NOW, before the upload's await —
    // otherwise the insertion lands wherever the cursor happens to be once a
    // multi-megabyte upload finishes, not where the user pasted.
    const range = captureCaretRange();
    void uploadAndInsertImage(file, range);
  }

  function handleDrop(e: React.DragEvent<HTMLTextAreaElement>) {
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (!file) return;
    e.preventDefault();
    // Same reasoning as handlePaste: capture synchronously, before the await.
    const range = captureCaretRange();
    void uploadAndInsertImage(file, range);
  }

  function buildAiPrompt(): string {
    const personaLine = selectedAuthor?.persona
      ? `Giọng văn của tác giả "${selectedAuthor.name}": ${selectedAuthor.persona}`
      : selectedAuthor
        ? `Tác giả: ${selectedAuthor.name} (chưa có persona riêng, dùng giọng chung ở trên).`
        : 'Chưa chọn tác giả.';
    const structureLine = `Viết bài với tiêu đề "${title || '(chưa có)'}", mô tả ngắn: "${excerpt || '(chưa có)'}", thẻ: ${tagsInput || '(chưa có)'}.`;
    return [HOUSE_STYLE_RULES, '', personaLine, '', MARKDOWN_CONVENTIONS, '', structureLine].join('\n');
  }

  async function handleCopyPrompt() {
    try {
      await navigator.clipboard.writeText(buildAiPrompt());
      toast.success('Đã sao chép prompt vào clipboard');
    } catch {
      toast.error('Không thể sao chép, vui lòng thử lại');
    }
  }

  if (!isNew && postQuery.isLoading) {
    return <p className="text-muted-foreground">Đang tải...</p>;
  }

  if (!isNew && (postQuery.error || !postQuery.data)) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-surface-600 hover:underline w-fit">
          <ArrowLeft className="h-4 w-4" /> Quay lại danh sách bài viết
        </Link>
        <p className="text-destructive">Không thể tải bài viết này. Vui lòng thử lại sau.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-surface-600 hover:underline w-fit">
        <ArrowLeft className="h-4 w-4" /> Quay lại danh sách bài viết
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">
              {isNew ? 'Bài viết mới' : title || '(Chưa có tiêu đề)'}
            </h1>
            {currentVisibility && (
              <Badge variant={VISIBILITY_VARIANT[currentVisibility]}>
                {currentVisibility === 'scheduled'
                  ? `${VISIBILITY_LABEL.scheduled} · ${formatDateTime(postQuery.data!.publishedAt as string)}`
                  : VISIBILITY_LABEL[currentVisibility]}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" disabled={!canSave || isSaving} onClick={handleSave}>
            {isSaving ? 'Đang lưu...' : 'Lưu'}
          </Button>
          {!isNew && (
            <>
              <Button type="button" disabled={!canSave || updatePost.isPending} onClick={handlePublish}>
                {willBeFuture ? 'Đặt lịch' : 'Đăng'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={currentVisibility === 'draft' || updatePost.isPending}
                onClick={handleUnpublish}
              >
                Gỡ xuống
              </Button>
              <Button type="button" variant="destructive" onClick={() => setDeleteOpen(true)}>
                Xoá
              </Button>
            </>
          )}
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xoá bài viết</AlertDialogTitle>
            <AlertDialogDescription>
              Bài viết "{title}" sẽ bị xoá vĩnh viễn. Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePost.isPending}>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              disabled={deletePost.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              Xoá bài viết
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader>
          <CardTitle className="font-display font-semibold tracking-tight text-surface-900">
            Thông tin bài viết
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="post-title">Tiêu đề</Label>
            <Input id="post-title" value={title} onChange={(e) => handleTitleChange(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="post-slug">Slug</Label>
            <Input id="post-slug" value={slug} onChange={(e) => handleSlugChange(e.target.value)} />
            {/* slugifyVi returns '' for an emoji-only or non-Latin (e.g. 日本語)
             *  title — nothing else points at this field, and Lưu just stays
             *  disabled with no explanation. */}
            {title.trim() && !slug.trim() && (
              <p className="text-xs text-destructive">
                Không thể tự tạo slug từ tiêu đề này, vui lòng nhập slug bằng tay (chỉ chữ thường, số và gạch
                ngang).
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="post-author">Tác giả</Label>
            <div className="flex items-center gap-2">
              <Select value={authorId} onValueChange={setAuthorId} disabled={authors.length === 0}>
                <SelectTrigger id="post-author" className="flex-1">
                  <SelectValue placeholder={authors.length === 0 ? 'Chưa có tác giả nào' : 'Chọn tác giả'} />
                </SelectTrigger>
                <SelectContent>
                  {authors.map((author) => (
                    <SelectItem key={author.id} value={author.id}>
                      {author.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Without this, an empty author list leaves the disabled
               *  select as a dead end — nothing points at where to fix it,
               *  and Lưu stays disabled with no explanation. */}
              {authors.length === 0 && (
                <Button asChild type="button" variant="outline" size="sm" className="shrink-0">
                  <Link to="/blog/authors">Tạo tác giả</Link>
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="post-excerpt">Mô tả ngắn</Label>
            <Textarea id="post-excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="post-tags">Thẻ (phân tách bằng dấu phẩy)</Label>
            <Input
              id="post-tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="ví dụ: điểm danh, hoá đơn"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="post-cover">Ảnh bìa (URL)</Label>
            <Input
              id="post-cover"
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="post-publish-at">
              Thời gian xuất bản{' '}
              <span className="text-xs font-normal text-muted-foreground">({timeZone})</span>
            </Label>
            <Input
              id="post-publish-at"
              type="datetime-local"
              className="max-w-64"
              value={publishInput}
              onChange={(e) => setPublishInput(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Để trống nghĩa là đăng ngay khi bấm "Đăng". Chọn thời gian trong tương lai để đặt lịch.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="font-display font-semibold tracking-tight text-surface-900">Nội dung</CardTitle>
          <Button type="button" variant="ghost" size="sm" onClick={() => setGuideOpen((v) => !v)}>
            Hướng dẫn viết
            {guideOpen ? <ChevronUp className="ml-1.5 h-4 w-4" /> : <ChevronDown className="ml-1.5 h-4 w-4" />}
          </Button>
        </CardHeader>
        {guideOpen && (
          <CardContent className="border-b border-surface-100 pb-4 text-sm text-surface-600">
            <div className="flex flex-col gap-3">
              <p className="whitespace-pre-line">{MARKDOWN_CONVENTIONS}</p>
              <p className="text-surface-500">
                Dán hoặc kéo-thả một ảnh vào khung soạn bên dưới để tự động tải lên và chèn{' '}
                <code className="rounded bg-surface-100 px-1">![](url)</code> tại vị trí con trỏ. Ảnh bìa là một
                trường riêng, không phải ảnh trong nội dung.
              </p>
              <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => void handleCopyPrompt()}>
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Sao chép prompt AI
              </Button>
            </div>
          </CardContent>
        )}
        <CardContent className="grid gap-4 pt-4 lg:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="post-body">Markdown</Label>
            <Textarea
              id="post-body"
              ref={textareaRef}
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              onPaste={handlePaste}
              onDrop={handleDrop}
              rows={20}
              className="font-mono text-sm"
              placeholder="Viết nội dung bài viết bằng Markdown..."
              // Belt-and-suspenders, not the fix: the actual fix is that
              // uploadAndInsertImage inserts at the range captured *before*
              // this upload started, not wherever the caret ends up.
              disabled={uploadMedia.isPending}
            />
          </div>
          <div className="space-y-2">
            <Label>Xem trước</Label>
            <div className="min-h-[480px] rounded-md border border-input bg-surface-50 p-4">
              {bodyMd.trim() ? (
                <div className={PREVIEW_CLASSNAME} dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <p className="text-sm text-muted-foreground">Bản xem trước sẽ hiện ở đây.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
